import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CalendarDaysIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  XCircleIcon,
  LockClosedIcon,
  MapPinIcon,
  ShareIcon
} from '@heroicons/react/24/outline';
import { PencilIcon, StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { formatDistanceToNow } from 'date-fns';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { 
  fetchOpportunity, 
  applyToOpportunity, 
  withdrawApplication,
  fetchMyApplications 
} from '../store/slices/opportunitiesSlice';
import Button from '../components/common/Button';
import BackButton from '../components/common/BackButton';
import MasonryCollabGrid from '../components/profile/MasonryCollabGrid';
import SubscriptionToggle from '../components/opportunities/SubscriptionToggle';
import { opportunityAPI } from '../services/api';
import LocationText from '../components/common/LocationText';
import Avatar from '../components/common/Avatar';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/format';
import SEOHead from '../components/common/SEOHead';
import LoginPromptModal from '../components/common/LoginPromptModal';
import ShareModal from '../components/common/ShareModal';
import { resolveImageUrl } from '../utils/image';
import { OPPORTUNITY_TYPES as OPP_CONSTANTS } from '../constants/opportunities';

const OpportunityDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const { currentOpportunity, loading, myApplications } = useAppSelector((state) => state.opportunities);
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [applying, setApplying] = useState(false);

  // Auto-fill application message when modal opens
  useEffect(() => {
    if (showApplicationModal && currentOpportunity && user) {
      const defaultMessage = generateDefaultMessage(currentOpportunity, user, 'application');
      setApplicationMessage(defaultMessage);
    }
  }, [showApplicationModal, currentOpportunity, user]);

  // Login Prompt Modal State (for guests)
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalMessage, setLoginModalMessage] = useState('');

  // Scroll detection for sticky header
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyHeader(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const hasApplied = React.useMemo(() => {
    if (!currentOpportunity || !user || !Array.isArray(myApplications)) return false;
    return myApplications.some(app => app.opportunity_id === currentOpportunity.id);
  }, [currentOpportunity, user, myApplications]);

  const applicationStatus = React.useMemo(() => {
    if (!currentOpportunity || !user || !Array.isArray(myApplications)) return null;
    const application = myApplications.find(app => app.opportunity_id === currentOpportunity.id);
    return application ? application.status : null;
  }, [currentOpportunity, user, myApplications]);

  useEffect(() => {
    if (id) {
      dispatch(fetchOpportunity(id));
      opportunityAPI.trackView(id).catch(error => {
        console.error('Failed to track view:', error);
      });
    }
    if (user) {
      dispatch(fetchMyApplications());
    }
  }, [dispatch, id, user]);

  const handleWithdraw = () => {
    setShowWithdrawModal(true);
  };

  const confirmWithdraw = async () => {
    if (!id || !user) return;
    
    try {
      await dispatch(withdrawApplication({ opportunityId: id, userId: user.id })).unwrap();
      setShowWithdrawModal(false);
    } catch (error) {
      console.error('Failed to withdraw:', error);
    }
  };

  const handleApply = async () => {
    if (!id || !currentOpportunity) return;
    
    setApplying(true);
    try {
      await dispatch(applyToOpportunity({
        id,
        data: { message: applicationMessage }
      })).unwrap();
      
      setShowApplicationModal(false);
      setApplicationMessage('');
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Failed to apply:', error);
      toast.error('Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy link:', err);
      toast.error('Failed to copy link');
    }
  };

  const handleMessageCreator = () => {
    if (!currentOpportunity?.creator || !user) return;

    const defaultMessage = generateDefaultMessage(currentOpportunity, user, 'accepted');
    
    navigate('/messages', {
      state: {
        openConversationWith: currentOpportunity.creator.id,
        userName: `${currentOpportunity.creator.first_name} ${currentOpportunity.creator.last_name}`,
        userAvatar: currentOpportunity.creator.profile_pic,
        userType: currentOpportunity.creator.user_type,
        prefilledMessage: defaultMessage,
        opportunityContext: {
          id: currentOpportunity.id,
          title: currentOpportunity.title,
          type: currentOpportunity.opportunity_type
        }
      }
    });
  };

  const generateDefaultMessage = (opportunity: any, currentUser: any, context: 'application' | 'accepted' = 'application') => {
    const creatorName = opportunity.creator.first_name;
    const userType = currentUser.user_type;
    const opportunityType = opportunity.opportunity_type;
    const opportunityTitle = opportunity.title;
    
    let greeting = `Hi ${creatorName}! 👋\n\n`;
    let mainMessage = '';
    let closing = `\n\nBest regards,\n${currentUser.first_name}`;

    if (context === 'accepted') {
      greeting = `Hi ${creatorName}! \n\n`;
      mainMessage = `Thanks for accepting my application for "${opportunityTitle}"! I'm excited to collaborate. \n\nWhen would be a good time to discuss the details?`;
      closing = `\n\nCheers,\n${currentUser.first_name}`;
    } else {
      // Application context
      const isCreatorOpportunity = [
        OPP_CONSTANTS.CREATOR,
        OPP_CONSTANTS.PHOTOGRAPHER,
        OPP_CONSTANTS.VIDEOGRAPHER,
        OPP_CONSTANTS.BOTH_CREATORS
      ].includes(opportunityType);
      
      if (isCreatorOpportunity && userType === 'creator') {
        const creatorRole = currentUser.creator_sub_type === 'photographer' 
          ? 'photographer' 
          : currentUser.creator_sub_type === 'videographer' 
            ? 'videographer' 
            : currentUser.creator_sub_type === 'both' 
              ? 'photographer / videographer' 
              : 'creator';
        
        mainMessage = `I came across your opportunity "${opportunityTitle}" and I'm really interested! 📸\n\nAs a ${creatorRole}, I believe I could be a great fit for this project because... [Share your relevant experience here]`;
      } else if (opportunityType === OPP_CONSTANTS.MODEL && userType === 'model') {
        mainMessage = `I saw your opportunity "${opportunityTitle}" and I'm very interested in being part of this project! ✨\n\nAs a ${userType}, I'm excited about the concept because... [Tell them why you love this concept]`;
      } else {
        const role = userType === 'creator' 
          ? (currentUser.creator_sub_type === 'photographer' ? 'photographer' : currentUser.creator_sub_type === 'videographer' ? 'videographer' : currentUser.creator_sub_type === 'both' ? 'photographer / videographer' : 'creator')
          : userType;
        mainMessage = `I found your opportunity "${opportunityTitle}" really interesting! \n\nAs a ${role}, I'd love to learn more about this project. I think I'm a good match because... [Explain why you are a good fit]`;
      }

      if (opportunity.location) {
        mainMessage += `\n\nI noticed you're working in ${opportunity.location} - that works perfectly for me!`;
      }
      
      closing = `\nLooking forward to hearing from you!\n\nBest regards,\n${currentUser.first_name}`;
    }

    return greeting + mainMessage + closing;
  };

  if (loading) {
    return (
      <div className="w-full mx-auto p-6 bg-white dark:bg-gray-900">
        <div className="animate-pulse space-y-8">
          <div className="h-64 bg-gray-200 dark:bg-gray-800 rounded-xl w-full"></div>
          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-2 space-y-4">
              <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-full"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-full"></div>
            </div>
            <div className="h-64 bg-gray-200 dark:bg-gray-800 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentOpportunity) {
    return (
      <div className="w-full mx-auto p-6 text-center py-20">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Opportunity not found</h1>
        <Button onClick={() => navigate('/opportunities')}>
          Browse Opportunities
        </Button>
      </div>
    );
  }



  // Unified Theme Colors (Teal & Orange)
  const theme = {
    bg: 'from-teal-50/50 to-white dark:from-gray-900 dark:to-gray-900',
    accent: 'text-teal-600 dark:text-teal-400',
    button: 'bg-teal-600 hover:bg-teal-700 shadow-teal-500/20 dark:shadow-none',
    light: 'bg-teal-50 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
    border: 'border-teal-100 dark:border-teal-900/30',
    orangeLight: 'bg-orange-50 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    orangeAccent: 'text-orange-600 dark:text-orange-400'
  };

  const isOwner = user?.id === currentOpportunity.creator_id;
  const matchInfo = (currentOpportunity as any).match_info;

  const getOpportunityTypeLabel = (type: string) => {
    switch (type) {
      case OPP_CONSTANTS.CREATOR: return 'Creator Needed';
      case OPP_CONSTANTS.PHOTOGRAPHER: return 'Photographer Needed';
      case OPP_CONSTANTS.VIDEOGRAPHER: return 'Videographer Needed';
      case OPP_CONSTANTS.BOTH_CREATORS: return 'Photographer & Videographer Needed';
      case OPP_CONSTANTS.MODEL: return 'Model Needed';
      case OPP_CONSTANTS.BOTH: return 'Creator & Model Needed';
      case OPP_CONSTANTS.CREATIVE_COLLABORATION: return 'Creative Collaboration';
      default: return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20 md:pb-0">
      {/* SEO Meta Tags */}
      <SEOHead
        title={`${currentOpportunity.title} | Photography & Creative Opportunity`}
        description={`${currentOpportunity.description?.substring(0, 140)}... Posted by ${currentOpportunity.creator?.first_name} ${currentOpportunity.creator?.last_name}${currentOpportunity.location ? ` in ${currentOpportunity.location}` : ''}. Apply now on ClickCollab - the #1 platform for photographer and model collaborations.`}
        image={resolveImageUrl(currentOpportunity.images?.[0])}
        url={typeof window !== 'undefined' ? window.location.href : ''}
        type="article"
        keywords={[
          currentOpportunity.opportunity_type?.replace(/_/g, ' ') || '',
          ...(currentOpportunity.tags || []),
          currentOpportunity.location || '',
          `${currentOpportunity.creator?.first_name} ${currentOpportunity.creator?.last_name}`,
          'photography opportunity',
          'modeling opportunity',
          'creative collaboration',
          'photographer wanted',
          'model wanted',
          'photo shoot opportunity',
          currentOpportunity.is_paid ? 'paid photography job' : 'TFP opportunity',
          'photography gig',
          'modeling gig'
        ].filter(Boolean)}
        author={currentOpportunity.creator ? `${currentOpportunity.creator.first_name} ${currentOpportunity.creator.last_name}` : undefined}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'JobPosting',
          title: currentOpportunity.title,
          description: currentOpportunity.description,
          datePosted: currentOpportunity.created_at,
          hiringOrganization: {
            '@type': 'Person',
            name: currentOpportunity.creator ? `${currentOpportunity.creator.first_name} ${currentOpportunity.creator.last_name}` : ''
          },
          jobLocation: {
            '@type': 'Place',
            address: currentOpportunity.location
          }
        }}
      />
      {/* Sticky Mobile/Desktop Header */}
      <AnimatePresence>
        {showStickyHeader && (
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            className="fixed top-0 left-0 right-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-md z-[100] border-b border-gray-200 dark:border-gray-800 h-16"
          >
            <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 overflow-hidden">
                <BackButton showLabel={false} className="flex-shrink-0" />
                <h2 className="text-sm md:text-lg font-bold text-gray-900 dark:text-white truncate">
                  {currentOpportunity.title}
                </h2>
              </div>
              
              <div className="flex items-center gap-2 flex-shrink-0">
                {!isOwner && !hasApplied && user && user.user_type !== 'hirer' && (
                  <Button
                    size="sm"
                    onClick={() => setShowApplicationModal(true)}
                    className="bg-teal-600 hover:bg-teal-700 text-white shadow-lg shadow-teal-500/30 rounded-full px-4"
                  >
                    Apply Now
                  </Button>
                )}
                {isOwner && (
                   <Link to={`/opportunities/${id}/edit`}>
                    <Button variant="outline" size="sm" className="hidden md:flex items-center gap-2">
                      <PencilIcon className="h-4 w-4" />
                      Edit
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <div className={`bg-gradient-to-b ${theme.bg} border-b ${theme.border}`}>
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb & Actions */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <BackButton showLabel={false} className="bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 shadow-sm" />
              <nav className="hidden sm:flex items-center space-x-2 text-sm">
                <Link to="/opportunities" className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">Opportunities</Link>
                <span className="text-gray-300 dark:text-gray-600">/</span>
                <span className="text-gray-900 dark:text-white font-medium truncate max-w-xs">{currentOpportunity.title}</span>
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowShareModal(true)} className="bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 shadow-sm">
                <ShareIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </Button>
              {isOwner && (
                <Link to={`/opportunities/${id}/edit`}>
                  <Button variant="outline" size="sm" className="bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 shadow-sm flex items-center gap-2">
                    <PencilIcon className="h-4 w-4" />
                    Edit
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Hero Content */}
          <div className="flex flex-col md:flex-row justify-between items-start gap-6">
            <div className="flex-1">
              <div className="flex flex-wrap gap-3 mb-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${theme.light}`}>
                  {getOpportunityTypeLabel(currentOpportunity.opportunity_type)}
                </span>
                {currentOpportunity.is_paid && (
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${theme.orangeLight}`}>
                    Paid Opportunity
                  </span>
                )}
                {!currentOpportunity.is_public && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                    <LockClosedIcon className="h-3 w-3 mr-1" />
                    Private
                  </span>
                )}
              </div>
              
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
                {currentOpportunity.title}
              </h1>

              <div className="flex flex-wrap items-center gap-6 text-gray-600 dark:text-gray-300 text-sm md:text-base">
                <LocationText value={currentOpportunity.location} className="flex items-center gap-2" iconClassName="h-5 w-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <div className="flex items-center gap-2">
                  <ClockIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <span>Posted {formatDistanceToNow(new Date(currentOpportunity.created_at), { addSuffix: true })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <UserIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <span>{currentOpportunity.application_count} Applicants</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Match Analysis Card */}
            {matchInfo && !isOwner && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-teal-100 dark:border-teal-900/30 overflow-hidden relative"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-teal-50 rounded-full -mr-16 -mt-16 opacity-50"></div>
                
                <div className="flex items-center justify-between mb-6 relative z-10">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      Why this matches you
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Based on your profile and preferences</p>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="text-3xl font-bold text-teal-600">{matchInfo.match_score}%</div>
                    <div className="text-xs font-medium text-teal-800 dark:text-teal-300 uppercase tracking-wide">Match Score</div>
                  </div>
                </div>

                <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 mb-6">
                  <div 
                    className="bg-gradient-to-r from-teal-400 to-teal-600 h-2 rounded-full transition-all duration-1000"
                    style={{ width: `${matchInfo.match_score}%` }}
                  ></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {matchInfo.matching_factors && matchInfo.matching_factors.map((factor: string, index: number) => (
                    <div key={index} className="flex items-center gap-3 bg-teal-50/50 dark:bg-teal-900/10 p-3 rounded-xl border border-teal-100/50 dark:border-teal-900/20">
                      <div className="bg-teal-100 dark:bg-teal-900/30 p-1.5 rounded-full">
                        <CheckCircleIcon className="h-4 w-4 text-teal-600" />
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{factor}</span>
                    </div>
                  ))}
                  {matchInfo.negative_factors && matchInfo.negative_factors.map((factor: string, index: number) => (
                    <div key={`neg-${index}`} className="flex items-center gap-3 bg-red-50/50 dark:bg-red-900/10 p-3 rounded-xl border border-red-100/50 dark:border-red-900/20">
                      <div className="bg-red-100 dark:bg-red-900/30 p-1.5 rounded-full">
                        <XCircleIcon className="h-4 w-4 text-red-600" />
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{factor}</span>
                    </div>
                  ))}
                  {matchInfo.distance_km !== undefined && matchInfo.distance_km !== null && (
                    <div className={`flex items-center gap-3 p-3 rounded-xl border ${
                      matchInfo.distance_km > 60 
                        ? 'bg-red-50/50 dark:bg-red-900/10 border-red-100/50 dark:border-red-900/20' 
                        : 'bg-teal-50/50 dark:bg-teal-900/10 border-teal-100/50 dark:border-teal-900/20'
                    }`}>
                      <div className={`p-1.5 rounded-full ${
                        matchInfo.distance_km > 60 
                          ? 'bg-red-100 dark:bg-red-900/30' 
                          : 'bg-teal-100 dark:bg-teal-900/30'
                      }`}>
                        <MapPinIcon className={`h-4 w-4 ${
                          matchInfo.distance_km > 60 ? 'text-red-600' : 'text-teal-600'
                        }`} />
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {matchInfo.distance_km < 1 ? 'Less than 1km away' : `${matchInfo.distance_km.toFixed(1)}km away`}
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Description */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-sm border border-gray-100 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">About the Project</h2>
              <div className="prose prose-lg prose-gray dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                {currentOpportunity.description}
              </div>
            </div>

            {/* Requirements */}
            {currentOpportunity.requirements && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-sm border border-gray-100 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Requirements</h2>
                <div className="prose prose-lg prose-gray dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {currentOpportunity.requirements}
                </div>
              </div>
            )}

            {/* Tags */}
            {currentOpportunity.tags && currentOpportunity.tags.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-sm border border-gray-100 dark:border-gray-700">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Tags & Skills</h2>
                <div className="flex flex-wrap gap-2">
                  {currentOpportunity.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Gallery */}
            {currentOpportunity.images_signed && currentOpportunity.images_signed.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-sm border border-gray-100 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Reference Images</h2>
                <MasonryCollabGrid 
                  collabImages={currentOpportunity.images_signed} 
                  isOwnProfile={false} 
                />
              </div>
            )}
          </div>

          {/* Sidebar - Right Column */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Action Card - Sticky */}
            <div className="sticky top-24 space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg shadow-gray-200/50 dark:shadow-none border border-gray-100 dark:border-gray-700 p-6">
                <div className="mb-6">
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Budget</span>
                  <div className="flex items-baseline gap-1 mt-1">
                    {currentOpportunity.is_paid && currentOpportunity.budget ? (
                      <>
                        <span className="text-4xl font-bold text-gray-900 dark:text-white">{formatCurrency(currentOpportunity.budget)}</span>
                      </>
                    ) : (
                      <span className="text-3xl font-bold text-gray-900 dark:text-white capitalize">
                        {currentOpportunity.compensation_type === 'tfp' ? 'TFP' : 
                         currentOpportunity.compensation_type === 'negotiable' ? 'Negotiable' :
                         currentOpportunity.compensation_type === 'unpaid' ? 'Unpaid' :
                         currentOpportunity.compensation_type || 'Unpaid'}
                      </span>
                    )}
                  </div>
                  {currentOpportunity.compensation_type === 'tfp' && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Time for Prints</p>
                  )}
                </div>

                <div className="space-y-3 mb-6">
                  {isOwner ? (
                    <Link to={`/opportunities/${currentOpportunity.id}/applications`} className="block">
                      <Button variant="primary" className="w-full py-3 text-lg shadow-lg shadow-teal-500/20 bg-teal-600 hover:bg-teal-700">
                        Review Applications ({currentOpportunity.application_count})
                      </Button>
                    </Link>
                  ) : (
                    <>
                      {hasApplied ? (
                        <div className="space-y-3">
                          {applicationStatus === 'accepted' ? (
                            <div className="w-full bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 py-4 px-4 rounded-xl flex items-center justify-center gap-2 font-bold border border-green-100 dark:border-green-900/30">
                              <CheckCircleIcon className="h-6 w-6" />
                              Application Accepted!
                            </div>
                          ) : applicationStatus === 'rejected' ? (
                            <div className="w-full bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 py-4 px-4 rounded-xl flex items-center justify-center gap-2 font-bold border border-red-100 dark:border-red-900/30">
                              <XCircleIcon className="h-6 w-6" />
                              Application Rejected
                            </div>
                          ) : (
                            <div className="w-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 py-4 px-4 rounded-xl flex items-center justify-center gap-2 font-bold border border-blue-100 dark:border-blue-900/30">
                              <ClockIcon className="h-6 w-6" />
                              Application Sent
                            </div>
                          )}
                          
                          {applicationStatus === 'pending' && (
                            <Button 
                              variant="outline" 
                              className="w-full text-red-600 dark:text-red-400 border-red-200 dark:border-red-900/30 hover:bg-red-50 dark:hover:bg-red-900/20 py-2"
                              onClick={handleWithdraw}
                            >
                              Withdraw Application
                            </Button>
                          )}
                        </div>
                      ) : (
                        isAuthenticated ? (
                          user?.user_type === 'hirer' ? (
                            <div className="w-full bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 py-4 px-4 rounded-xl text-center border border-gray-200 dark:border-gray-600">
                              <p className="font-medium">Creator account required</p>
                              <p className="text-xs mt-1">Upgrade to apply</p>
                            </div>
                          ) : (
                            <Button
                              className="w-full justify-center py-4 text-lg font-bold shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl shadow-teal-500/30"
                              onClick={() => setShowApplicationModal(true)}
                            >
                              Apply Now
                            </Button>
                          )
                        ) : (
                          <Button
                            className="w-full justify-center py-4 text-lg font-bold shadow-xl transition-all duration-300 bg-teal-600 hover:bg-teal-700 text-white rounded-xl"
                            onClick={() => {
                              setLoginModalMessage('Sign in to apply for this opportunity');
                              setShowLoginModal(true);
                            }}
                          >
                            Log in to Apply
                          </Button>
                        )
                      )}
                      
                      {!isOwner && applicationStatus === 'accepted' && (
                        <Button
                          variant="outline"
                          className="w-full justify-center py-3 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-teal-600 dark:hover:text-teal-400 transition-colors duration-300 rounded-xl mt-3"
                          onClick={() => {
                            if (!user) {
                              setLoginModalMessage('Sign in to message this creator');
                              setShowLoginModal(true);
                              return;
                            }
                            handleMessageCreator();
                          }}
                        >
                          <ChatBubbleLeftIcon className="h-5 w-5 mr-2" />
                          Message Creator
                        </Button>
                      )}
                    </>
                  )}
                </div>

                <div className="space-y-4 pt-6 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded-lg">
                      <CalendarDaysIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {currentOpportunity.is_flexible_dates ? (
                          <span className="text-teal-600 dark:text-teal-400">Flexible Dates</span>
                        ) : (
                          <>
                            {currentOpportunity.start_date ? new Date(currentOpportunity.start_date).toLocaleDateString() : 'TBD'}
                            {currentOpportunity.end_date && ` - ${new Date(currentOpportunity.end_date).toLocaleDateString()}`}
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded-lg">
                      <ClockIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Duration</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{currentOpportunity.duration || 'Not specified'}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded-lg">
                      <BriefcaseIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Experience</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{currentOpportunity.experience_level || 'Any'}</span>
                    </div>
                  </div>
                </div>

                {/* SubscriptionToggle - COMMENTED OUT
                {!isOwner && user && (
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <SubscriptionToggle opportunityId={currentOpportunity.id} />
                  </div>
                )}
                */}
              </div>

              {/* Creator Card */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4">About the Creator</h3>
                <div className="flex items-center gap-4 mb-4">
                   <Link to={`/profile/${currentOpportunity.creator?.id}`}>
                    <Avatar
                      src={currentOpportunity.creator?.profile_pic_thumb_signed || currentOpportunity.creator?.profile_pic}
                      alt={`${currentOpportunity.creator?.first_name} ${currentOpportunity.creator?.last_name}`}
                      size="lg"
                      className="ring-4 ring-gray-50 dark:ring-gray-700"
                    />
                  </Link>
                  <div>
                    <Link 
                      to={`/profile/${currentOpportunity.creator?.id}`}
                      className="text-lg font-bold text-gray-900 dark:text-white hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                    >
                      {currentOpportunity.creator?.first_name} {currentOpportunity.creator?.last_name}
                    </Link>
                    {(currentOpportunity.creator?.review_count ?? 0) > 0 && (
                      <div className="flex items-center gap-1 text-amber-500 text-sm font-medium">
                        <StarIconSolid className="h-4 w-4" />
                        <span>{currentOpportunity.creator?.rating?.toFixed(1)}</span>
                        <span className="text-gray-400 dark:text-gray-500 font-normal">({currentOpportunity.creator?.review_count} reviews)</span>
                      </div>
                    )}
                  </div>
                
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-3 text-center">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">{currentOpportunity.creator?.project_count || 0}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Projects</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-3 text-center">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">{currentOpportunity.creator?.followers_count || 0}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Followers</div>
                  </div>
                </div>

                <Link to={`/profile/${currentOpportunity.creator?.id}`}>
                  <Button variant="outline" className="w-full rounded-xl border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                    View Profile
                  </Button>
                </Link>

                {/* Contact Info - COMMENTED OUT
                {(currentOpportunity.contact_email || currentOpportunity.contact_phone) && (
                  <div className="mt-6 pt-6 border-t border-gray-100 space-y-3">
                    {currentOpportunity.contact_email && (
                      <div className="flex items-center gap-3 text-sm">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                        <a href={`mailto:${currentOpportunity.contact_email}`} className="text-gray-600 hover:text-teal-600 truncate">
                          {currentOpportunity.contact_email}
                        </a>
                      </div>
                    )}
                    {currentOpportunity.contact_phone && (
                      <div className="flex items-center gap-3 text-sm">
                        <PhoneIcon className="h-4 w-4 text-gray-400" />
                        <a href={`tel:${currentOpportunity.contact_phone}`} className="text-gray-600 hover:text-teal-600">
                          {currentOpportunity.contact_phone}
                        </a>
                      </div>
                    )}
                  </div>
                )}
                */}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Application Modal */}
      {showApplicationModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
          >
            <div className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-700/50">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Apply to {currentOpportunity.title}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Introduce yourself and explain why you're a good fit.
              </p>
            </div>
            
            <div className="p-6">
              <textarea
                value={applicationMessage}
                onChange={(e) => setApplicationMessage(e.target.value)}
                placeholder="Hi, I'm interested in this opportunity because..."
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                autoFocus
              />
            </div>
            
            <div className="p-6 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowApplicationModal(false)}
                className="rounded-xl border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleApply}
                loading={applying}
                disabled={!applicationMessage.trim()}
                className="rounded-xl px-6 bg-teal-600 hover:bg-teal-700"
              >
                Send Application
              </Button>
            </div>
          </motion.div>
        </div>
      )}
      {/* Withdraw Confirmation Modal */}
      <AnimatePresence>
        {showWithdrawModal && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            >
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Withdraw Application?</h3>
                
                <p className="text-gray-600 dark:text-gray-400 mb-8">
                  Are you sure you want to withdraw? You can apply again later if the opportunity is still open.
                </p>
                
                <div className="flex justify-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowWithdrawModal(false)}
                    className="rounded-xl w-32 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    className="bg-red-600 hover:bg-red-700 border-red-600 dark:border-red-500 rounded-xl w-32"
                    onClick={confirmWithdraw}
                  >
                    Withdraw
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Login Prompt Modal for Guests */}
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        message={loginModalMessage}
      />
      {/* Share Modal */}
      {currentOpportunity && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title={currentOpportunity.title}
          url={window.location.href}
          type="opportunity"
          data={{
            id: currentOpportunity.id,
            title: currentOpportunity.title,
            subtitle: `${currentOpportunity.location || 'Remote'} • ${
              currentOpportunity.is_paid 
                ? 'Paid' 
                : currentOpportunity.compensation_type === 'tfp' 
                  ? 'TFP' 
                  : currentOpportunity.compensation_type === 'negotiable' 
                    ? 'Negotiable' 
                    : 'Unpaid'
            }`,
            image: currentOpportunity.creator?.profile_pic, // Or opportunity image if available
            is_paid: currentOpportunity.is_paid,
            budget: currentOpportunity.budget,
            location: currentOpportunity.location,
            compensation_type: currentOpportunity.compensation_type
          }}
        />
      )}
    </div>
  );
};

export default OpportunityDetailsPage;
